import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { Settings } from '../../../interfaces/Settings/Settings';
import { getSettingsByType, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';

export const MailServerSettings = () => {
  const [settings, setSettings] = useState<Settings[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshSettings = () => {
    setLoading(true);
    getSettingsByType(SettingsType.MAIL_SERVER)
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) =>
      setSettings(
        getUpdatedSettings(SettingsType.MAIL_SERVER, key, settings, newValue),
      ),
    [settings],
  );

  const handleSave = () => {
    setLoading(true);
    upsertSettings(settings)
      .then(() => {
        toast.success('Ayarlar başarıyla kaydedildi');
        refreshSettings();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '95%',
      }}
    >
      <Waiting open={loading} />
      <Typography
        sx={{
          ...sharedStyles.h5,
          fontWeight: 'bold',
        }}
      >
        E-Posta Ayarları
      </Typography>
      <Divider />

      <SettingsRow
        name='Gönderen Adresi'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'mail_from_address')}
        onChange={(newValue) => handleChange('mail_from_address', newValue)}
      />
      <SettingsRow
        name='Gönderen Adı'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'mail_from_name')}
        onChange={(newValue) => handleChange('mail_from_name', newValue)}
      />
      <SettingsRow
        name='Sunucu'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'mail_host')}
        onChange={(newValue) => handleChange('mail_host', newValue)}
      />
      <SettingsRow
        name='Bağlantı Noktası'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'mail_port')}
        onChange={(newValue) => handleChange('mail_port', newValue)}
      />
      <SettingsRow
        name='Kullanıcı Adı'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'mail_username')}
        onChange={(newValue) => handleChange('mail_username', newValue)}
      />
      <SettingsRow
        name='Şifre'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'mail_password')}
        onChange={(newValue) => handleChange('mail_password', newValue)}
        masked
      />
      <SettingsRow
        name='Şifreleme'
        valueType={SettingsValueType.SELECT}
        value={getSettingsValue(settings, 'mail_encryption')}
        onChange={(newValue) => handleChange('mail_encryption', newValue)}
        options={[
          { value: 'ssl', label: 'SSL' },
          { value: 'tls', label: 'TLS' },
        ]}
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }} />
      <Box display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
